import React, { useCallback, useRef, useEffect, useState } from 'react';
import { Input, Switch, Select, Icon, message, Button, Modal, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import Regex from '../../utils/regex';
import { Container, FieldTitle, FieldContent, RowSection, CustomIcon } from './styles';
import { SquareBoxIcon, CircleBoxIcon } from '../../artboard/CheckboxSettings/CheckBoxIcons';
import { CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR } from '../../artboard/CheckboxSettings/transactionValues';
import { FILLABLE_ELEMENT_TEXTBOX_SETTINGS_SIDEBAR } from '../FillableSettings/translationValues';
import { FIELD_NAME_KEY, IS_CHECKED_KEY, OPTIONS_KEY, dummyCallback } from '../../utils/complexDocumentConstants';

const { Option } = Select;
const NAMESPACES = ['CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR'];
const defaultElementDetails = el => el.elementDetails;
const defaultSettingsGetter = el => el.elementDetails?.value?.formField;

const GroupCheckboxInput = ({ el, onOtherNameChange, selectOtherCheckbox, t }) => {
  const fieldNameRef = useRef(null);
  const forwardRef = useRef(null);

  useEffect(() => {
    if (forwardRef.current) {
      forwardRef.current = null;
    } else {
      if (fieldNameRef.current?.input && el[OPTIONS_KEY]) {
        fieldNameRef.current.input.value = el[OPTIONS_KEY][FIELD_NAME_KEY];
      }
    }
  }, [el]);

  const onOtherFieldNameChange = useCallback(
    (e, el) => {
      if (Regex.NO_SPLCHAR_EXCEPT_HYPHEN.test(e.target.value)) {
        message.error(
          t(
            FILLABLE_ELEMENT_TEXTBOX_SETTINGS_SIDEBAR.FIELD_NAME_SPECIAL_CHARACTER_ERROR_MESSAGE
              .key,
            FILLABLE_ELEMENT_TEXTBOX_SETTINGS_SIDEBAR.FIELD_NAME_SPECIAL_CHARACTER_ERROR_MESSAGE
              .value,
          ),
        );
        e.target.value = el[OPTIONS_KEY][FIELD_NAME_KEY];
      } else {
        forwardRef.current = e.target;
        onOtherNameChange(e.target.value, el);
      }
    },
    [onOtherNameChange],
  );

  return (
    <>
      <Input
        ref={fieldNameRef}
        defaultValue={el?.formOptions?.fieldName}
        onChange={e => onOtherFieldNameChange(e, el)}
      />
      <Tooltip title="Select checkbox">
        <Icon type="select" onClick={() => selectOtherCheckbox(el)} />
      </Tooltip>
    </>
  );
};

const FillableCheckBoxSettings = props => {
  const {
    element,
    isRadio,
    style = {},
    updateElementDetails,
    onFieldNameChange,
    onGroupNameChange,
    OPTIONS_KEY = '',
    FIELD_NAME_KEY = 'fieldName',
    CHECK_TYPE_KEY = 'checkType',
    IS_VARIABLE_KEY = 'isVariable',
    settingsGetter = defaultSettingsGetter,
    getElementDetails = defaultElementDetails,
    READ_ONLY_KEY = 'readOnly',
    addNewElement = dummyCallback,
    memoizedOtherFormElements = [],
    onOtherNameChange,
    onConfirmGroupNameChange,
    refreshSelectedElementIds,
    isInteractiveDesign
  } = props;
  const { t } = useTranslation(NAMESPACES);

  const [groupNameModalText, setGroupNameModalText] = useState('');
  const [groupName, setGroupName] = useState(element?.formOptions?.groupName);
  const forwardRef = useRef(null);
  const fieldNameRef = useRef(null);

  const fillableSettings = settingsGetter(element);
  const details = getElementDetails(element);

  useEffect(() => {
    if (forwardRef.current) {
      forwardRef.current = null;
    } else {
      if (fieldNameRef.current?.input && details[OPTIONS_KEY])
        fieldNameRef.current.input.value = details[OPTIONS_KEY][FIELD_NAME_KEY];
    }
  }, [details]);

  useEffect(() => {
    setGroupName(fillableSettings.groupName);
  }, [fillableSettings.groupName]);

  const onRadioGroupNameChange = useCallback(
    e => {
      const isGroupElAlreadyAvailaibale = memoizedOtherFormElements.find(
        el => el.formOptions.groupName === e.target.value,
      );
      if (isGroupElAlreadyAvailaibale) {
        setGroupNameModalText(e.target.value);
      } else {
        if (e.target.value.split('.').length <= 1) {
          setGroupName(e.target.value);
          onGroupNameChange(e.target.value);
        } 
      }
    },
    [onGroupNameChange, memoizedOtherFormElements],
  );

  const onTextInputChange = useCallback(
    e => {
      if (Regex.NO_SPLCHAR_EXCEPT_HYPHEN.test(e.target.value)) {
        message.error(
          t(
            FILLABLE_ELEMENT_TEXTBOX_SETTINGS_SIDEBAR.FIELD_NAME_SPECIAL_CHARACTER_ERROR_MESSAGE
              .key,
            FILLABLE_ELEMENT_TEXTBOX_SETTINGS_SIDEBAR.FIELD_NAME_SPECIAL_CHARACTER_ERROR_MESSAGE
              .value,
          ),
        );
        e.target.value = details[OPTIONS_KEY][FIELD_NAME_KEY];
      } else {
        forwardRef.current = e.target;
        onFieldNameChange(e.target.value);
      }
    },
    [onFieldNameChange, details],
  );

  const selectOtherCheckbox = element => {
    refreshSelectedElementIds(element.uuid);
  };

  const onModalCancel = () => {
    setGroupNameModalText('');
    setGroupName(groupName);
  };

  const onModalConfirm = () => {
    setGroupName(groupNameModalText);
    onConfirmGroupNameChange(groupNameModalText);
    setGroupNameModalText('');
  };

  const otherGroupCheckboxes = memoizedOtherFormElements?.filter(
    el => el?.formOptions?.groupName === element?.formOptions?.groupName,
  );

  return (
    <Container style={style}>
      {!isRadio && (
        <FieldTitle isTitle={1}>
          {t(
            CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.CHECK_BOX_SETTINGS_TITLE.key,
            CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.CHECK_BOX_SETTINGS_TITLE.value,
          )}
        </FieldTitle>
      )}
      {isRadio && (
        <FieldTitle isTitle={1}>
          {t(
            CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.RADIO_BUTTON_SETTINGS_TITLE.key,
            CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.RADIO_BUTTON_SETTINGS_TITLE.value,
          )}
        </FieldTitle>
      )}
      {
        <RowSection>
          <FieldTitle>
            {t(
              CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.GROUP_NAME_LABEL.key,
              CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.GROUP_NAME_LABEL.value,
            )}
          </FieldTitle>
          <FieldContent>
            <Input
              value={groupName}
              placeholder={t(
                CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.GROUP_NAME_PLACEHOLDER.key,
                CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.GROUP_NAME_PLACEHOLDER.value,
              )}
              onChange={onRadioGroupNameChange}
            />
          </FieldContent>
        </RowSection>
      }
      <RowSection>
        <FieldTitle>
          {t(
            CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.FIELD_NAME_LABEL.key,
            CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.FIELD_NAME_LABEL.value,
          )}
        </FieldTitle>
        <FieldContent>
          <Input
            ref={fieldNameRef}
            defaultValue={fillableSettings[FIELD_NAME_KEY]}
            placeholder={t(
              CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.FIELD_NAME_PLACEHOLDER.key,
              CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.FIELD_NAME_PLACEHOLDER.value,
            )}
            onChange={onTextInputChange}
          />
        </FieldContent>
      </RowSection>
      <RowSection>
        <FieldTitle>
          {t(
            CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.CHECKED_LABEL.key,
            CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.CHECKED_LABEL.value,
          )}
        </FieldTitle>
        <FieldContent>
          <Switch
            checkedChildren={t(
              CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.DYNAMIC_VALUE_YES_SWITCH.key,
              CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.DYNAMIC_VALUE_YES_SWITCH.value,
            )}
            unCheckedChildren={t(
              CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.DYNAMIC_VALUE_NO_SWITCH.key,
              CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.DYNAMIC_VALUE_NO_SWITCH.value,
            )}
            checked={details?.formOptions?.isChecked}
            onChange={e => updateElementDetails(e, IS_CHECKED_KEY)}
          ></Switch>
        </FieldContent>
      </RowSection>
      <RowSection>
        <FieldTitle>
          {t(
            CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.IS_DYNAMIC_LABEL.key,
            CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.IS_DYNAMIC_LABEL.value,
          )}
        </FieldTitle>
        <FieldContent>
          <Switch
            checkedChildren={t(
              CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.DYNAMIC_VALUE_YES_SWITCH.key,
              CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.DYNAMIC_VALUE_YES_SWITCH.value,
            )}
            unCheckedChildren={t(
              CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.DYNAMIC_VALUE_NO_SWITCH.key,
              CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.DYNAMIC_VALUE_NO_SWITCH.value,
            )}
            checked={details.style[IS_VARIABLE_KEY]}
            onChange={e => updateElementDetails(e, IS_VARIABLE_KEY)}
            disabled={isInteractiveDesign}
          />
        </FieldContent>
      </RowSection>
      {!isRadio && (
        <RowSection>
          <FieldTitle>
            {t(
              CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.CHECK_STYLE_LABEL.key,
              CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.CHECK_STYLE_LABEL.value,
            )}
          </FieldTitle>
          <FieldContent>
            <Select
              value={fillableSettings[CHECK_TYPE_KEY]}
              style={{ width: 100 }}
              onChange={e => updateElementDetails(e, CHECK_TYPE_KEY)}
            >
              <Option value={'4'}>
                <CustomIcon type={'check-square'} />
              </Option>
              <Option value={'8'}>
                <CustomIcon type={'close-square'} />
              </Option>
              <Option value={'n'}>
                <CustomIcon component={SquareBoxIcon} />
              </Option>
              <Option value={'l'}>
                <CustomIcon component={CircleBoxIcon} />
              </Option>
            </Select>
          </FieldContent>
        </RowSection>
      )}
      <RowSection>
        <FieldTitle>
          {t(
            CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.READ_ONLY_LABEL.key,
            CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.READ_ONLY_LABEL.value,
          )}
        </FieldTitle>
        <FieldContent>
          <Switch
            checkedChildren={t(
              CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.READ_ONLY_YES_SWITCH.key,
              CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.READ_ONLY_YES_SWITCH.value,
            )}
            unCheckedChildren={t(
              CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.READ_ONLY_NO_SWITCH.key,
              CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.READ_ONLY_NO_SWITCH.value,
            )}
            checked={fillableSettings[READ_ONLY_KEY]}
            onChange={e => updateElementDetails(e, READ_ONLY_KEY)}
          />
        </FieldContent>
      </RowSection>
      <RowSection>
        <FieldTitle>
          <Button onClick={addNewElement} type="primary">
            {isRadio
              ? t(
                  CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.ADD_MORE_RADIOBUTTON.key,
                  CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.ADD_MORE_RADIOBUTTON.value,
                )
              : t(
                  CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.ADD_MORE_CHECKBOX.key,
                  CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.ADD_MORE_CHECKBOX.value,
                )}
          </Button>
        </FieldTitle>
      </RowSection>
      {otherGroupCheckboxes?.length ? (
        <RowSection style={{ flexDirection: 'column', height: 'unset' }}>
          <FieldTitle style={{ width: '100%' }}>Group Checkboxes</FieldTitle>
          <FieldContent
            style={{
              flexDirection: 'column',
              width: '100%',
              alignItems: 'unset',
              paddingLeft: '20px',
            }}
          >
            <ol style={{ display: 'flex', flexDirection: 'column', rowGap: '20px' }}>
              {otherGroupCheckboxes.map(el => (
                <li
                  style={{ display: 'flex', columnGap: '20px', alignItems: 'center' }}
                  key={el?.uuid}
                >
                  <GroupCheckboxInput
                    el={el}
                    onOtherNameChange={onOtherNameChange}
                    selectOtherCheckbox={selectOtherCheckbox}
                  />
                </li>
              ))}
            </ol>
          </FieldContent>
        </RowSection>
      ) : (
        <></>
      )}
      {groupNameModalText && (
        <Modal
          width="600px"
          centered
          title={null}
          maskClosable={false}
          visible={!!groupNameModalText}
          onCancel={onModalCancel}
          footer={[
            <Button onClick={onModalCancel}>
              {t(
                CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.CANCEL_LABEL.key,
                CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.CANCEL_LABEL.value,
              )}
            </Button>,
            <Button type="primary" onClick={onModalConfirm}>
              {t(
                CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.OK_LABEL.key,
                CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.OK_LABEL.value,
              )}
            </Button>,
          ]}
          keyboard={false}
        >
          <div style={{ fontSize: '18px', margin: '20px 0px' }}>
            {isRadio
              ? `There is already an radio button group with same group name. Do you want to apply the
            changes from that radio button group?`
              : `There is already an checkbox group with same group name. Do you want to apply the
            changes from that checkbox group?`}
          </div>
        </Modal>
      )}
    </Container>
  );
};

export default FillableCheckBoxSettings;
